import type { ListMoviesRequest, ListPeopleRequest, ListTermsRequest, MovieMark, UpdateMarkRequest } from '@zyx/shared';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApi } from '@/hooks/use-connection';
import { queryKeys } from '@/lib/query-keys';

/** A grid of movies that loads the next page on demand. */
export function useMovieList(request: Omit<ListMoviesRequest, 'page'>) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: queryKeys.movieList(request),
    queryFn: ({ pageParam }) => api.listMovies({ ...request, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
  });
}

export function useMovie(code: string) {
  const api = useApi();
  return useQuery({ queryKey: queryKeys.movie(code), queryFn: () => api.getMovie(code) });
}

export function useContinueWatching() {
  const api = useApi();
  return useQuery({ queryKey: queryKeys.continueWatching(), queryFn: () => api.listContinueWatching({ limit: 24 }) });
}

export function useHistory() {
  const api = useApi();
  return useQuery({ queryKey: [...queryKeys.movies(), 'history'], queryFn: () => api.listHistory(100) });
}

export function usePerson(personId: number) {
  const api = useApi();
  return useQuery({ queryKey: queryKeys.person(personId), queryFn: () => api.getPerson(personId) });
}

export function useTerm(termId: number) {
  const api = useApi();
  return useQuery({ queryKey: queryKeys.term(termId), queryFn: () => api.getTerm(termId) });
}

export function usePeople(request: ListPeopleRequest) {
  const api = useApi();
  return useQuery({ queryKey: ['people', 'list', request], queryFn: () => api.listPeople(request) });
}

export function useTerms(request: ListTermsRequest) {
  const api = useApi();
  return useQuery({ queryKey: queryKeys.terms(request), queryFn: () => api.listTerms(request) });
}

export function useStats() {
  const api = useApi();
  return useQuery({ queryKey: queryKeys.stats(), queryFn: () => api.getStats() });
}

export type MarkChange = Omit<UpdateMarkRequest, 'code'> & { readonly inWatchlist?: boolean };

/**
 * Changes how the viewer feels about a movie. The watchlist is its own endpoint, but
 * to a caller it is one more field of the mark. Every movie list is refreshed after,
 * since a mark decides what several of them show.
 */
export function useUpdateMark(code: string) {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (change: MarkChange): Promise<MovieMark> => {
      const { inWatchlist, ...mark } = change;
      let latest: MovieMark | undefined;
      if (Object.keys(mark).length > 0) {
        latest = (await api.updateMark({ code, ...mark })).mark;
      }
      if (inWatchlist !== undefined) {
        latest = (inWatchlist ? await api.addToWatchlist(code) : await api.removeFromWatchlist(code)).mark;
      }
      if (latest === undefined) {
        throw new Error('Nothing to change.');
      }
      return latest;
    },
    onSuccess: async (_mark, change) => {
      if (change.hidden === true) {
        toast.success(`${code} is hidden. Find it again under Library → Hidden.`);
      }
      await client.invalidateQueries({ queryKey: queryKeys.movies() });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'The change was not saved.');
    },
  });
}

/** Favourites a person, or not; every view of people is refreshed after. */
export function useFavouritePerson(personId: number) {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (favourite: boolean) => api.updatePersonMark({ personId, favourite }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['people'] }),
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'The change was not saved.');
    },
  });
}
